import { useReducer, useEffect, useRef, useState } from 'react';
import {
    Box, Grid, TextField, Button, Typography, CircularProgress, Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel,
    Radio, RadioGroup, FormControl, FormLabel, Chip, Switch, Select, MenuItem,
    InputLabel, Pagination, Autocomplete
} from '@mui/material';
import TableInfo from '../../components/TableInfo';
import api from '../../services/api';
import { maskCNPJ, maskCEP, maskTEL } from '../../utils/mask';
import { Assessment, Person, FileDownload } from '@mui/icons-material';
import axios from 'axios';
import * as qs from 'qs';
import XLSX from 'xlsx';
import { validateBr } from 'js-brasil';
import { useLocation, useNavigate } from 'react-router-dom';

const AFFILIATE_TYPES = {
    RPPS_ASSOCIADO: 'RPPS Associado',
    RPPS_OUTROS_ESTADOS: 'RPPS de Outros Estados',
    PATROCINADOR: 'Patrocinador',
    NAO_ASSOCIADO: 'Não Associado',
    ISENTO: 'Isento',
    STAFF: 'Staff'
};

const SPONSOR_TYPE = 'PATROCINADOR';
const SPONSOR_QUOTA_OPTIONS = [
    { label: 'Master', value: 10 },
    { label: 'Diamante', value: 8 },
    { label: 'Platinum', value: 6 },
    { label: 'Ouro', value: 4 }
];

const RELATED_ROW_BG = '#E3F2FD';
const RELATIONSHIP_TYPES = [
    { value: 'PREFEITURA', label: 'Prefeitura' },
    { value: 'CAMARA', label: 'Câmara' },
    { value: 'AUTARQUIA_1', label: 'Autarquia 1' },
    { value: 'AUTARQUIA_2', label: 'Autarquia 2' }
];

const createRelatedInstitution = (data = {}) => ({
    id: data.id || Date.now() + Math.floor(Math.random() * 10000),
    cnpj: data.cnpj || '',
    tipo: data.tipo || '',
    nomeEmpresa: data.nomeEmpresa || '',
    endereco: data.endereco || '',
    cidade: data.cidade || '',
    uf: data.uf || '',
    cep: data.cep ? maskCEP(data.cep) : '',
    telefone: data.telefone ? maskTEL(data.telefone) : '',
    email: data.email || '',
    loading: false,
    errors: {}
});

// Estado inicial
const initialState = {
    affiliates: [],
    loading: false,
    exportingExcel: false,
    searchingCompany: false,
    searchingCEP: false,
    filters: {
        name: '',
        city: '',
        document: '',
        type: '',
        state: '',
        reference: ''
    },
    pagination: {
        page: 1,
        pageSize: 50,
        total: 0,
        pageCount: 0
    },
    stateAlert: {
        status: false,
        type: 'success',
        msg: ''
    },
    dialog: {
        open: false,
        editing: null
    },
    form: {
        social_reason: '',
        document: '',
        affiliate: true,
        state: '',
        city: '',
        email: '',
        president: '',
        whatsapp: '',
        cep: '',
        address: '',
        district: '',
        number: '',
        phone: '',
        trade: '',
        type: '',
        quotas: 0,
        active: true,
        observations: '',
        blocked: false
        ,
        instituicoesRelacionadas: []
    },
    fieldsEnabled: false,
    companyFound: false
};

// Reducer
function affiliateReducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };

        case 'SET_EXPORTING_EXCEL':
            return { ...state, exportingExcel: action.payload };

        case 'SET_SEARCHING_COMPANY':
            return { ...state, searchingCompany: action.payload };

        case 'SET_SEARCHING_CEP':
            return { ...state, searchingCEP: action.payload };

        case 'SET_AFFILIATES':
            return { ...state, affiliates: action.payload };

        case 'SET_PAGINATION':
            return { ...state, pagination: { ...state.pagination, ...action.payload } };

        case 'SET_FILTERS':
            return { ...state, filters: { ...state.filters, ...action.payload } };

        case 'RESET_FILTERS':
            return { ...state, filters: initialState.filters };

        case 'SET_ALERT':
            return { ...state, stateAlert: action.payload };

        case 'CLOSE_ALERT':
            return { ...state, stateAlert: { ...state.stateAlert, status: false } };

        case 'OPEN_DIALOG':
            return {
                ...state,
                dialog: { open: true, editing: action.payload.editing },
                form: action.payload.form,
                fieldsEnabled: action.payload.fieldsEnabled,
                companyFound: action.payload.companyFound
            };

        case 'CLOSE_DIALOG':
            return {
                ...state,
                dialog: { open: false, editing: null },
                form: initialState.form,
                fieldsEnabled: false,
                companyFound: false
            };

        case 'UPDATE_FORM':
            return { ...state, form: { ...state.form, ...action.payload } };

        case 'RESET_FORM':
            return { ...state, form: initialState.form };

        case 'SET_COMPANY_SEARCH_RESULT':
            return {
                ...state,
                form: { ...state.form, ...action.payload.form },
                fieldsEnabled: action.payload.fieldsEnabled,
                companyFound: action.payload.companyFound
            };

        case 'COMPANY_NOT_FOUND':
            return {
                ...state,
                fieldsEnabled: true,
                companyFound: false
            };

        default:
            return state;
    }
}

export default function Affiliates({ sponsorsOnly = false } = {}) {
    const [state, dispatch] = useReducer(affiliateReducer, initialState);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const query = useRef();
    const location = useLocation();
    const navigate = useNavigate();
    const deepLinkHandledRef = useRef(false);
    const availableAffiliateTypes = sponsorsOnly
        ? { [SPONSOR_TYPE]: AFFILIATE_TYPES[SPONSOR_TYPE] }
        : Object.fromEntries(Object.entries(AFFILIATE_TYPES).filter(([key]) => key !== SPONSOR_TYPE));
    const pageTitle = sponsorsOnly ? 'Gestão de Patrocinadores' : 'Gestão de Filiados';
    const tableTitle = sponsorsOnly ? 'Patrocinadores' : 'Filiados';
    const entityLabel = sponsorsOnly ? 'Patrocinador' : 'Filiado';
    const entityLabelLower = sponsorsOnly ? 'patrocinador' : 'afiliado';

    // Função para buscar cidades pelo estado usando API IBGE
    const searchCitiesByState = async (stateCode, cityName) => {
        if (!stateCode || cityName.length < 3) {
            setCitySuggestions([]);
            return;
        }

        try {
            setLoadingCities(true);
            // Busca municípios pelo estado na API do IBGE
            const response = await axios.get(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`);

            if (response.data && Array.isArray(response.data)) {
                // Filtrar cidades que contêm o texto digitado
                const filteredCities = response.data
                    .map(municipio => municipio.nome)
                    .filter(nome => nome.toLowerCase().includes(cityName.toLowerCase()))
                    .slice(0, 10); // Limitar a 10 sugestões

                setCitySuggestions(filteredCities);
            } else {
                setCitySuggestions([]);
            }
        } catch (error) {
            console.log('Erro ao buscar cidades:', error);
            setCitySuggestions([]);
        } finally {
            setLoadingCities(false);
        }
    };

    // Função para buscar dados pelo CEP
    const handleCep = async (cep) => {
        const cleanCep = cep.replace(/\D/g, '');

        // Atualiza o CEP com máscara
        dispatch({
            type: 'UPDATE_FORM',
            payload: { cep: maskCEP(cep) }
        });

        if (cleanCep.length >= 8) {
            try {
                dispatch({ type: 'SET_SEARCHING_CEP', payload: true });
                const searchByCep = `https://cep.awesomeapi.com.br/json/${cleanCep}`;
                const dataByCep = await axios.get(searchByCep);

                console.log(dataByCep.data);

                dispatch({
                    type: 'UPDATE_FORM',
                    payload: {
                        cep: maskCEP(cep),
                        city: dataByCep.data.city,
                        state: dataByCep.data.state,
                        district: dataByCep.data.district,
                        address: dataByCep.data.address
                    }
                });

                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'success',
                        msg: 'CEP encontrado! Dados de endereço preenchidos automaticamente.'
                    }
                });
            } catch (error) {
                console.log(error);
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'error',
                        msg: 'CEP não encontrado ou inválido.'
                    }
                });
            } finally {
                dispatch({ type: 'SET_SEARCHING_CEP', payload: false });
            }
        }
    };

    // Função para buscar empresa por CNPJ
    const searchCompanyByCNPJ = async (document) => {
        try {
            dispatch({ type: 'SET_SEARCHING_COMPANY', payload: true });
            query.current = qs.stringify({
                filters: {
                    document: { $eq: document }
                },
                sort: ['createdAt:desc'],
                populate: '*',
                encodeValuesOnly: true,
            });

            const response = await api.get('/companies?' + query.current);

            if (response.data.data && response.data.data.length > 0) {
                const company = response.data.data[0].attributes;

                if (!company) {
                    dispatch({
                        type: 'SET_ALERT',
                        payload: {
                            status: true,
                            type: 'warning',
                            msg: 'Empresa não encontrada.'
                        }
                    });
                    dispatch({ type: 'COMPANY_NOT_FOUND' });
                    return;
                }

                const updateForm = {
                    social_reason: company.social_reason || '',
                    president: company.president || '',
                    state: company.state || '',
                    city: company.city || '',
                    email: company.email || '',
                    whatsapp: company.whatsapp || '',
                    cep: company.cep || '',
                    address: company.address || '',
                    district: company.district || '',
                    number: company.number || '',
                    phone: company.phone || '',
                    trade: company.trade || '',
                    document: document,
                    type: sponsorsOnly
                        ? SPONSOR_TYPE
                        : (!company.state.includes('PR') ? 'RPPS_OUTROS_ESTADOS' : state.form.type)
                };

                dispatch({
                    type: 'SET_COMPANY_SEARCH_RESULT',
                    payload: {
                        form: updateForm,
                        fieldsEnabled: false,
                        companyFound: true
                    }
                });

                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'success',
                        msg: 'Empresa encontrada! Dados preenchidos automaticamente.'
                    }
                });
            } else {
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'warning',
                        msg: 'Empresa não encontrada.'
                    }
                });
                dispatch({ type: 'COMPANY_NOT_FOUND' });
            }
        } catch (error) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Erro ao buscar empresa.'
                }
            });
            dispatch({ type: 'COMPANY_NOT_FOUND' });
        } finally {
            dispatch({ type: 'SET_SEARCHING_COMPANY', payload: false });
        }
    };

    const fetchCompanyByDocument = async (document) => {
        if (!document) return null;

        const masked = maskCNPJ(document);
        const cnpjEncoded = masked.replace(/\//g, '%2F');

        try {
            const byCnpjResponse = await api.get(`/companies/${cnpjEncoded}`);
            if (byCnpjResponse.data) {
                return byCnpjResponse.data;
            }
        } catch (error) {
            // fallback para queries abaixo
        }

        try {
            const queryMasked = qs.stringify({
                filters: { document: { $eq: masked } },
                sort: ['createdAt:desc'],
                populate: '*',
                encodeValuesOnly: true
            });
            const responseMasked = await api.get('/companies?' + queryMasked);
            const companyMasked = responseMasked.data?.data?.[0]?.attributes;
            if (companyMasked) return companyMasked;
        } catch (error) {
            // fallback para documento sem máscara
        }

        try {
            const unmasked = masked.replace(/\D/g, '');
            const queryUnmasked = qs.stringify({
                filters: { document: { $eq: unmasked } },
                sort: ['createdAt:desc'],
                populate: '*',
                encodeValuesOnly: true
            });
            const responseUnmasked = await api.get('/companies?' + queryUnmasked);
            return responseUnmasked.data?.data?.[0]?.attributes || null;
        } catch (error) {
            return null;
        }
    };

    // Função para processar dados dos afiliados e adicionar instituições relacionadas
    const processAffiliatesData = async (companiesData) => {
        const processedData = [];

        // Buscar todas as referências de affiliate-payments
        const affiliateIds = companiesData
            .filter(company => company.attributes.affiliate?.data?.id)
            .map(company => company.attributes.affiliate.data.id);

        console.log('Processing affiliates:', {
            totalCompanies: companiesData.length,
            affiliateIds: affiliateIds.length,
            ids: affiliateIds
        });

        let paymentsMap = new Map();

        if (affiliateIds.length > 0) {
            try {
                // Buscar todos os affiliate-payments sem filtro e filtrar no frontend
                let allPayments = [];
                let currentPage = 1;
                let hasMorePages = true;

                while (hasMorePages) {
                    const paymentResponse = await api.get(
                        `/affiliate-payments?pagination[page]=${currentPage}&pagination[pageSize]=100&populate=affiliate&fields[0]=reference`
                    );

                    if (paymentResponse.data.data.length > 0) {
                        allPayments.push(...paymentResponse.data.data);
                        hasMorePages = currentPage < paymentResponse.data.meta.pagination.pageCount;
                        currentPage++;
                    } else {
                        hasMorePages = false;
                    }
                }

                // Filtrar apenas os payments dos affiliateIds que temos
                const affiliateIdsSet = new Set(affiliateIds);
                const filteredPayments = allPayments.filter(payment => {
                    const affiliateId = payment.attributes.affiliate?.data?.id;
                    return affiliateId && affiliateIdsSet.has(affiliateId);
                });

                // Mapear referências por affiliate ID
                filteredPayments.forEach(payment => {
                    const affiliateId = payment.attributes.affiliate?.data?.id;
                    const reference = payment.attributes.reference;

                    if (affiliateId && reference) {
                        if (!paymentsMap.has(affiliateId)) {
                            paymentsMap.set(affiliateId, new Set());
                        }
                        paymentsMap.get(affiliateId).add(reference);
                    }
                });

                console.log('Payments found:', {
                    totalPayments: allPayments.length,
                    filteredPayments: filteredPayments.length,
                    uniqueAffiliates: paymentsMap.size,
                    paymentsMap: Array.from(paymentsMap.entries()).map(([id, refs]) => ({
                        affiliateId: id,
                        references: Array.from(refs)
                    }))
                });
            } catch (error) {
                console.error('Erro ao buscar payments:', error);
            }
        }

        // Processar cada company
        for (const company of companiesData) {
            const affiliateData = company.attributes.affiliate?.data;
            const affiliateId = affiliateData?.id;
            const referenceSet = affiliateId && paymentsMap.has(affiliateId)
                ? paymentsMap.get(affiliateId)
                : new Set();
            const references = referenceSet.size > 0 ? Array.from(referenceSet).sort().join(', ') : '-';
            const hasReference = referenceSet.size > 0;

            // Log para debug
            if (hasReference) {
                console.log('Company with references:', {
                    social_reason: company.attributes.social_reason,
                    affiliateId,
                    references,
                    hasReference,
                    referenceSetSize: referenceSet.size
                });
            }

            // Adicionar company principal
            processedData.push({
                ...company,
                attributes: {
                    ...company.attributes,
                    hasReference: hasReference,
                    references: references
                }
            });

            // Adicionar instituições relacionadas se existirem
            const instituicoes = affiliateData?.attributes?.instituicoesRelacionadas;
            if (instituicoes && Array.isArray(instituicoes)) {
                instituicoes.forEach((instituicao, index) => {
                    processedData.push({
                        id: `${company.id}-inst-${index}`,
                        attributes: {
                            social_reason: instituicao.nomeEmpresa,
                            document: instituicao.cnpj,
                            state: company.attributes.state,
                            city: company.attributes.city, // Cidade da empresa principal
                            email: instituicao.email || company.attributes.email,
                            phone: instituicao.telefone || company.attributes.phone,
                            trade: '',
                            affiliate: {
                                data: {
                                    id: affiliateId,
                                    attributes: {
                                        type: instituicao.tipo,
                                        active: affiliateData.attributes.active,
                                        blocked: affiliateData.attributes.blocked,
                                        quotas: 0
                                    }
                                }
                            },
                            isRelatedInstitution: true,
                            parentCompany: company.attributes.social_reason,
                            hasReference: referenceSet.size > 0,
                            references: references
                        }
                    });
                });
            }
        }

        // Função de ordenação personalizada
        const getTypeSortOrder = (type) => {
            const order = {
                'RPPS_ASSOCIADO': 1,
                'NAO_ASSOCIADO': 2,
                'RPPS_OUTROS_ESTADOS': 3,
                'PATROCINADOR': 4,
                'STAFF': 5,
                'ISENTO': 6
            };
            return order[type] || 999;
        };

        // Ordenar: primeiro com referência, depois sem referência (ambos seguindo ordem de tipo)
        processedData.sort((a, b) => {
            // Prioridade 1: Quem tem referência vem primeiro
            const aHasReference = a.attributes.hasReference ? 0 : 1;
            const bHasReference = b.attributes.hasReference ? 0 : 1;

            if (aHasReference !== bHasReference) {
                return aHasReference - bHasReference;
            }

            // Prioridade 2: Ordem por tipo
            const aType = a.attributes.affiliate?.data?.attributes?.type || '';
            const bType = b.attributes.affiliate?.data?.attributes?.type || '';

            return getTypeSortOrder(aType) - getTypeSortOrder(bType);
        });

        return processedData;
    };

    // Função para carregar dados com filtro de referência (ano de affiliate-payment)
    const handleLoadWithReference = async (page = 1) => {
        try {
            // Buscar affiliate-payments pela referência
            const paymentParams = {
                filters: {
                    reference: { $containsi: state.filters.reference }
                },
                populate: {
                    affiliate: {
                        populate: {
                            company: {
                                fields: ['id', 'social_reason', 'document', 'state', 'city', 'email', 'phone']
                            }
                        }
                    }
                },
                pagination: {
                    page: 1,
                    pageSize: 100
                }
            };

            const paymentQuery = qs.stringify(paymentParams);
            const paymentResponse = await api.get(`/affiliate-payments?${paymentQuery}`);

            // Extrair IDs únicos de companies dos affiliate-payments
            const companyIds = new Set();
            paymentResponse.data.data.forEach(payment => {
                const companyId = payment.attributes?.affiliate?.data?.attributes?.company?.data?.id;
                if (companyId) {
                    companyIds.add(companyId);
                }
            });

            if (companyIds.size === 0) {
                dispatch({ type: 'SET_AFFILIATES', payload: [] });
                dispatch({
                    type: 'SET_PAGINATION',
                    payload: {
                        page: 1,
                        pageSize: state.pagination.pageSize,
                        total: 0,
                        pageCount: 0
                    }
                });
                return;
            }

            // Buscar companies pelos IDs encontrados
            const companyParams = {
                populate: {
                    affiliate: {
                        fields: ['type', 'active', 'blocked', 'quotas', 'observations', 'instituicoesRelacionadas']
                    }
                },
                sort: ['createdAt:desc'],
                pagination: {
                    page,
                    pageSize: state.pagination.pageSize
                },
                filters: {
                    id: { $in: Array.from(companyIds) }
                }
            };

            // Aplicar filtros adicionais
            if (state.filters.type === 'NAO_ASSOCIADO') {
                companyParams.filters.affiliate = { id: { $null: true } };
            } else if (state.filters.type) {
                companyParams.filters.affiliate = { type: { $eq: state.filters.type } };
            }

            if (state.filters.name) {
                companyParams.filters['social_reason'] = { $containsi: state.filters.name };
            }
            if (state.filters.document) {
                companyParams.filters['document'] = { $containsi: state.filters.document };
            }
            if (state.filters.state) {
                companyParams.filters['state'] = { $eq: state.filters.state };
            }
            if (state.filters.city) {
                companyParams.filters['city'] = { $containsi: state.filters.city };
            }

            const companyQuery = qs.stringify(companyParams, {
                skipNulls: true,
                skipUndefined: true,
                skipEmptyStrings: true
            });

            const response = await api.get(`/companies?${companyQuery}`);

            // Processar dados e adicionar instituições relacionadas
            const processedData = await processAffiliatesData(response.data.data);

            dispatch({ type: 'SET_AFFILIATES', payload: processedData });
            dispatch({
                type: 'SET_PAGINATION',
                payload: {
                    page: response.data.meta.pagination.page,
                    pageSize: response.data.meta.pagination.pageSize,
                    total: response.data.meta.pagination.total,
                    pageCount: response.data.meta.pagination.pageCount
                }
            });
        } catch (error) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || 'Erro ao buscar afiliados com referência.'
                }
            });
        }
    };

    // Função para carregar dados
    const handleLoad = async (page = 1) => {
        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const filters = {
                ...(state.filters.name && {
                    company: {
                        social_reason: { $containsi: state.filters.name }
                    }
                }),
                ...(state.filters.document && {
                    company: {
                        document: { $containsi: state.filters.document }
                    }
                }),
                ...(state.filters.state && {
                    company: {
                        state: { $eq: state.filters.state }
                    }
                }),
                ...(state.filters.city && {
                    company: {
                        city: { $containsi: state.filters.city }
                    }
                }),
                ...(state.filters.type && {
                    type: { $eq: state.filters.type }
                }),
                ...(sponsorsOnly
                    ? { type: { $eq: SPONSOR_TYPE } }
                    : (!state.filters.type ? { type: { $ne: SPONSOR_TYPE } } : {}))
            };

            query.current = qs.stringify({
                filters,
                pagination: {
                    page,
                    pageSize: state.pagination.pageSize
                },
                encodeValuesOnly: true,
            });

            const response = await api.get('/affiliates?' + query.current);

            // Se houver filtro de referência, buscar paginação personalizada
            let displayData = response.data.data;
            let paginationMeta = response.data.meta.pagination;

            if (state.filters.reference) {
                // Filtrar por referência
                displayData = response.data.data.filter(item => {
                    const references = item.attributes.references;
                    return references && references !== '-' && references.includes(state.filters.reference);
                });

                // Recalcular paginação para dados filtrados
                const startIndex = (page - 1) * state.pagination.pageSize;
                const endIndex = startIndex + state.pagination.pageSize;
                const paginatedData = displayData.slice(startIndex, endIndex);

                paginationMeta = {
                    page,
                    pageSize: state.pagination.pageSize,
                    total: displayData.length,
                    pageCount: Math.ceil(displayData.length / state.pagination.pageSize)
                };

                displayData = paginatedData;
            }

            dispatch({ type: 'SET_AFFILIATES', payload: displayData });
            dispatch({
                type: 'SET_PAGINATION',
                payload: {
                    page: paginationMeta.page,
                    total: paginationMeta.total,
                    pageCount: paginationMeta.pageCount
                }
            });
        } catch (error) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || 'Erro ao buscar empresas.'
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Função para abrir dialog
    const handleOpenDialog = async (affiliate = null) => {
        console.log(affiliate)
        if (affiliate) {
            // Buscar o afiliado na lista
            let editAffiliate = state.affiliates.find(a => a.id === affiliate);

            if (!editAffiliate) {
                try {
                    const response = await api.get(`/affiliates/${affiliate}?populate[company][populate]=*`);
                    const fetchedAffiliate = response.data?.data;

                    if (!fetchedAffiliate) {
                        throw new Error('Afiliado não encontrado');
                    }

                    const companyData = fetchedAffiliate.attributes?.company?.data;
                    const companyAttributes = companyData?.attributes || {};

                    editAffiliate = {
                        id: companyData?.id || fetchedAffiliate.id,
                        attributes: {
                            social_reason: companyAttributes.social_reason || '',
                            president: companyAttributes.president || '',
                            document: companyAttributes.document || '',
                            state: companyAttributes.state || '',
                            city: companyAttributes.city || '',
                            email: companyAttributes.email || '',
                            whatsapp: companyAttributes.whatsapp || '',
                            cep: companyAttributes.cep || '',
                            address: companyAttributes.address || '',
                            district: companyAttributes.district || '',
                            number: companyAttributes.number || '',
                            phone: companyAttributes.phone || '',
                            trade: companyAttributes.trade || '',
                            affiliate: {
                                data: {
                                    id: fetchedAffiliate.id,
                                    attributes: {
                                        ...fetchedAffiliate.attributes
                                    }
                                }
                            }
                        }
                    };
                } catch (error) {
                    dispatch({
                        type: 'SET_ALERT',
                        payload: {
                            status: true,
                            type: 'error',
                            msg: 'Não foi possível carregar os dados do afiliado para edição.'
                        }
                    });
                    return;
                }
            }

            console.log(editAffiliate)

            let formData = {};

            // Se for uma instituição relacionada, buscar dados atuais da company
            if (editAffiliate?.attributes?.isRelatedInstitution) {
                try {
                    // Obter CNPJ da instituição relacionada
                    const cnpj = editAffiliate.attributes.document;

                    if (!cnpj) {
                        throw new Error('CNPJ não encontrado');
                    }

                    dispatch({ type: 'SET_SEARCHING_COMPANY', payload: true });

                    // Buscar dados atuais da company pelo CNPJ
                    const query = qs.stringify({
                        filters: {
                            document: { $eq: cnpj }
                        },
                        populate: '*'
                    });

                    const response = await api.get(`/companies?${query}`);

                    if (!response.data.data || response.data.data.length === 0) {
                        throw new Error('Empresa não encontrada pelo CNPJ');
                    }

                    const companyData = response.data.data[0];

                    formData = {
                        social_reason: companyData.attributes.social_reason || '',
                        president: companyData.attributes.president || '',
                        document: companyData.attributes.document || '',
                        state: companyData.attributes.state || '',
                        city: companyData.attributes.city || '',
                        email: companyData.attributes.email || '',
                        whatsapp: companyData.attributes.whatsapp || '',
                        cep: companyData.attributes.cep || '',
                        address: companyData.attributes.address || '',
                        district: companyData.attributes.district || '',
                        number: companyData.attributes.number || '',
                        phone: companyData.attributes.phone || '',
                        trade: companyData.attributes.trade || '',
                        type: editAffiliate.attributes.affiliate?.data?.attributes?.type || 'Não Associado',
                        quotas: editAffiliate.attributes.affiliate?.data?.attributes?.quotas || 0,
                        active: editAffiliate.attributes.affiliate?.data?.attributes?.active ?? true,
                        blocked: editAffiliate.attributes.affiliate?.data?.attributes?.blocked || false,
                        observations: editAffiliate.attributes.affiliate?.data?.attributes?.observations || ''
                    };

                    dispatch({ type: 'SET_SEARCHING_COMPANY', payload: false });
                } catch (error) {
                    console.error('Erro ao buscar dados da instituição relacionada:', error);
                    dispatch({ type: 'SET_SEARCHING_COMPANY', payload: false });
                    dispatch({
                        type: 'SET_ALERT',
                        payload: {
                            status: true,
                            type: 'error',
                            msg: 'Erro ao buscar dados atuais da instituição relacionada.'
                        }
                    });
                    return;
                }
            } else {
                // Dados normais do afiliado
                const relatedInstitutions = editAffiliate.attributes.affiliate?.data?.attributes?.instituicoesRelacionadas;
                let hydratedRelatedInstitutions = Array.isArray(relatedInstitutions)
                    ? relatedInstitutions.map(item => createRelatedInstitution(item))
                    : [];

                if (hydratedRelatedInstitutions.length > 0) {
                    dispatch({ type: 'SET_SEARCHING_COMPANY', payload: true });
                    hydratedRelatedInstitutions = await Promise.all(
                        hydratedRelatedInstitutions.map(async (item) => {
                            if (!validateBr.cnpj(item.cnpj)) return item;

                            const company = await fetchCompanyByDocument(item.cnpj);
                            if (!company) return item;

                            return {
                                ...item,
                                nomeEmpresa: company.social_reason || item.nomeEmpresa,
                                endereco: company.address || item.endereco,
                                cidade: company.city || item.cidade,
                                uf: company.state || item.uf,
                                cep: company.cep ? maskCEP(company.cep) : item.cep,
                                telefone: company.phone ? maskTEL(company.phone) : item.telefone,
                                email: company.email || item.email
                            };
                        })
                    );
                    dispatch({ type: 'SET_SEARCHING_COMPANY', payload: false });
                }

                formData = {
                    social_reason: editAffiliate.attributes.social_reason || '',
                    president: editAffiliate.attributes.president || '',
                    document: editAffiliate.attributes.document || '',
                    state: editAffiliate.attributes.state || '',
                    city: editAffiliate.attributes.city || '',
                    email: editAffiliate.attributes.email || '',
                    whatsapp: editAffiliate.attributes.whatsapp || '',
                    cep: editAffiliate.attributes.cep || '',
                    address: editAffiliate.attributes.address || '',
                    district: editAffiliate.attributes.district || '',
                    number: editAffiliate.attributes.number || '',
                    phone: editAffiliate.attributes.phone || '',
                    trade: editAffiliate.attributes.trade || '',
                    type: editAffiliate.attributes.affiliate?.data?.attributes?.type || 'Não Associado',
                    quotas: editAffiliate.attributes.affiliate?.data?.attributes?.quotas || 0,
                    active: editAffiliate.attributes.affiliate?.data?.attributes?.active ?? true,
                    blocked: editAffiliate.attributes.affiliate?.data?.attributes?.blocked || false,
                    observations: editAffiliate.attributes.affiliate?.data?.attributes?.observations || '',
                    instituicoesRelacionadas: hydratedRelatedInstitutions
                };
            }

            dispatch({
                type: 'OPEN_DIALOG',
                payload: {
                    editing: editAffiliate,
                    form: formData,
                    fieldsEnabled: true,
                    companyFound: true
                }
            });
        } else {
            dispatch({
                type: 'OPEN_DIALOG',
                payload: {
                    editing: null,
                    form: {
                        ...initialState.form,
                        type: sponsorsOnly ? SPONSOR_TYPE : ''
                    },
                    fieldsEnabled: false,
                    companyFound: false
                }
            });
        }
    };

    // Função para salvar
    const handleSave = async () => {
        // Validações
        if (!state.form.document || state.form.document.length < 18) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'CNPJ é obrigatório e deve estar completo!'
                }
            });
            return;
        }

        if (!state.form.social_reason || !state.form.state || !state.form.city || !state.form.email || !state.form.cep) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Todos os campos obrigatórios devem ser preenchidos!'
                }
            });
            return;
        }

        if (state.dialog.editing && !state.dialog.editing?.attributes?.isRelatedInstitution) {
            const relatedWithErrors = state.form.instituicoesRelacionadas.map((item) => {
                const errors = {};
                if (!item.tipo) errors.tipo = 'Tipo é obrigatório';
                if (!item.cnpj) {
                    errors.cnpj = 'CNPJ é obrigatório';
                } else if (!validateBr.cnpj(item.cnpj)) {
                    errors.cnpj = 'CNPJ inválido';
                }
                if (!item.nomeEmpresa) errors.nomeEmpresa = 'Nome da empresa é obrigatório';
                if (!item.endereco) errors.endereco = 'Endereço é obrigatório';
                if (!item.cidade) errors.cidade = 'Cidade é obrigatória';
                if (!item.uf) {
                    errors.uf = 'UF é obrigatória';
                } else if (item.uf !== state.form.state) {
                    errors.uf = `A UF deve ser ${state.form.state}`;
                }
                if (!item.cep) errors.cep = 'CEP é obrigatório';
                if (!item.telefone) errors.telefone = 'Telefone é obrigatório';
                if (!item.email) errors.email = 'Email é obrigatório';

                return { ...item, errors };
            });

            const hasErrors = relatedWithErrors.some((item) => Object.keys(item.errors).length > 0);
            if (hasErrors) {
                dispatch({
                    type: 'UPDATE_FORM',
                    payload: { instituicoesRelacionadas: relatedWithErrors }
                });
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'error',
                        msg: 'Corrija os erros das instituições relacionadas antes de salvar.'
                    }
                });
                return;
            }
        }

        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const payload = {
                company: {
                    social_reason: state.form.social_reason,
                    president: state.form.president,
                    document: state.form.document,
                    state: state.form.state,
                    city: state.form.city,
                    email: state.form.email,
                    whatsapp: state.form.whatsapp,
                    cep: state.form.cep,
                    address: state.form.address,
                    district: state.form.district,
                    number: state.form.number,
                    phone: state.form.phone,
                    trade: state.form.trade,
                },
                type: sponsorsOnly ? SPONSOR_TYPE : state.form.type,
                quotas: (sponsorsOnly || state.form.type === SPONSOR_TYPE) ? state.form.quotas : 0,
                active: state.form.active,
                blocked: state.form.blocked,
                observations: state.form.observations
            };
            if (state.dialog.editing && !state.dialog.editing?.attributes?.isRelatedInstitution) {
                payload.instituicoesRelacionadas = (state.form.instituicoesRelacionadas || []).map((item) => ({
                    cnpj: item.cnpj,
                    tipo: item.tipo,
                    nomeEmpresa: item.nomeEmpresa,
                    endereco: item.endereco,
                    cidade: item.cidade,
                    uf: item.uf,
                    cep: item.cep,
                    telefone: item.telefone,
                    email: item.email
                }));
            }
            const editingAffiliateId = state.dialog.editing?.attributes?.affiliate?.data?.id;

            if (state.dialog.editing && editingAffiliateId) {
                await api.put(`/affiliates/${editingAffiliateId}`, { data: { ...payload } });
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'success',
                        msg: `${entityLabel} atualizado com sucesso!`
                    }
                });
            } else {
                await api.post('/affiliates', { data: { ...payload } });
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'success',
                        msg: `${entityLabel} cadastrado com sucesso!`
                    }
                });
            }

            dispatch({ type: 'CLOSE_DIALOG' });
            handleLoad(state.pagination.page);
        } catch (error) {
            console.log(error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || `Erro ao salvar ${entityLabelLower}.`
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Função para deletar
    const handleDelete = async (id) => {
        // Verificar se é uma instituição relacionada
        const affiliateToDelete = state.affiliates.find(a => a.id === id);
        if (affiliateToDelete?.attributes?.isRelatedInstitution) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Instituições relacionadas não podem ser excluídas diretamente. Edite o afiliado principal.'
                }
            });
            return;
        }

        if (!window.confirm(`Deseja realmente excluir este ${entityLabelLower}?`)) return;

        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            await api.delete(`/affiliates/${id}`);

            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'success',
                    msg: `${entityLabel} excluído com sucesso!`
                }
            });

            if (state.affiliates.length === 1 && state.pagination.page > 1) {
                const newPage = state.pagination.page - 1;
                dispatch({ type: 'SET_PAGINATION', payload: { page: newPage } });
                handleLoad(newPage);
            } else {
                handleLoad(state.pagination.page);
            }
        } catch (error) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: error.response?.data?.error?.message || `Erro ao excluir ${entityLabelLower}.`
                }
            });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    // Função para exportar Excel
    const handleExcel = async () => {
        if (state.affiliates.length <= 0) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Nenhum resultado para gerar planilha!'
                }
            });
            return;
        }

        dispatch({ type: 'SET_EXPORTING_EXCEL', payload: true });

        try {
            let page = 1;
            const pageSize = 100;
            let allAffiliates = [];

            // Busca todos os afiliados paginadamente
            while (true) {
                const params = {
                    populate: ['affiliate'],
                    sort: ['affiliate.type:asc','social_reason:asc', 'trade:asc','state:asc', 'city:asc'],
                    pagination: {
                        page,
                        pageSize
                    },
                    filters: {}
                };

                // Aplica os filtros atuais
                if (state.filters.type) {
                    params.filters.affiliate = { type: { $eq: state.filters.type } };
                } else if (sponsorsOnly) {
                    params.filters.affiliate = { type: { $eq: SPONSOR_TYPE } };
                } else {
                    params.filters.affiliate = { type: { $ne: SPONSOR_TYPE } };
                }
                if (state.filters.name) {
                    params.filters.social_reason = { $containsi: state.filters.name };
                }
                if (state.filters.document) {
                    params.filters.document = { $containsi: state.filters.document };
                }
                if (state.filters.city) {
                    params.filters.city = { $containsi: state.filters.city };
                }

                const queryString = qs.stringify(params, {
                    skipNulls: true,
                    skipUndefined: true,
                    skipEmptyStrings: true
                });

                const response = await api.get(`/companies?${queryString}`);
                const affiliatesData = response.data.data;

                if (!affiliatesData || affiliatesData.length === 0) break;

                allAffiliates.push(...affiliatesData);
                page++;
            }

            // Transforma os dados para a planilha, incluindo instituições relacionadas
            const sheetData = [];
            const relatedRowFlags = [];

            allAffiliates
                .filter(company => company.attributes.affiliate?.data)
                .forEach(company => {
                    const affiliate = company.attributes.affiliate.data.attributes;

                    sheetData.push({
                        razao_social: company.attributes.social_reason || '',
                        cnpj: company.attributes.document || '',
                        cidade: company.attributes.city || '',
                        nome_fantasia: company.attributes.trade || '',
                        tipo_filiado: AFFILIATE_TYPES[affiliate.type] || affiliate.type || 'Não Associado',
                        estado: company.attributes.state || '',
                        email: company.attributes.email || '',
                        telefone: company.attributes.phone || '',
                        cep: company.attributes.cep || '',
                        endereco: company.attributes.address || '',
                        numero: company.attributes.number || '',
                        bairro: company.attributes.district || '',
                        ativo: affiliate.active ? 'Sim' : 'Não',
                        bloqueado: affiliate.blocked ? 'Sim' : 'Não',
                        ...(sponsorsOnly ? { cotas: affiliate.quotas || 0 } : {}),
                        instituicao_relacionada: 'Não',
                        empresa_principal: '',
                        observacoes: affiliate.observations || '',
                        data_cadastro: new Date(company.attributes.createdAt).toLocaleDateString('pt-BR'),
                        data_atualizacao: new Date(company.attributes.updatedAt).toLocaleDateString('pt-BR')
                    });
                    relatedRowFlags.push(false);

                    const instituicoesRelacionadas = affiliate.instituicoesRelacionadas;
                    if (Array.isArray(instituicoesRelacionadas)) {
                        instituicoesRelacionadas.forEach((instituicao) => {
                            sheetData.push({
                                razao_social: instituicao.nomeEmpresa || '',
                                cnpj: instituicao.cnpj || '',
                                cidade: company.attributes.city || '',
                                nome_fantasia: company.attributes.trade || '',
                                tipo_filiado: AFFILIATE_TYPES[instituicao.tipo] || instituicao.tipo || '-',
                                estado: company.attributes.state || '',
                                email: instituicao.email || company.attributes.email || '',
                                telefone: instituicao.telefone || company.attributes.phone || '',
                                cep: company.attributes.cep || '',
                                endereco: company.attributes.address || '',
                                numero: company.attributes.number || '',
                                bairro: company.attributes.district || '',
                                ativo: affiliate.active ? 'Sim' : 'Não',
                                bloqueado: affiliate.blocked ? 'Sim' : 'Não',
                                ...(sponsorsOnly ? { cotas: 0 } : {}),
                                instituicao_relacionada: 'Sim',
                                empresa_principal: company.attributes.social_reason || '',
                                observacoes: affiliate.observations || '',
                                data_cadastro: new Date(company.attributes.createdAt).toLocaleDateString('pt-BR'),
                                data_atualizacao: new Date(company.attributes.updatedAt).toLocaleDateString('pt-BR')
                            });
                            relatedRowFlags.push(true);
                        });
                    }
                });

            // Gera e baixa o Excel
            const sheet = XLSX.utils.json_to_sheet(sheetData);

            // Define larguras das colunas
            const columnWidths = [
                { wch: 30 }, // razao_social
                { wch: 18 }, // cnpj
                { wch: 20 }, // cidade
                { wch: 25 }, // nome_fantasia
                { wch: 20 }, // tipo_filiado
                { wch: 10 }, // estado
                { wch: 30 }, // email
                { wch: 15 }, // telefone
                { wch: 12 }, // cep
                { wch: 30 }, // endereco
                { wch: 8 },  // numero
                { wch: 20 }, // bairro
                { wch: 8 },  // ativo
                { wch: 10 }, // bloqueado
                ...(sponsorsOnly ? [{ wch: 8 }] : []), // cotas
                { wch: 22 }, // instituicao_relacionada
                { wch: 30 }, // empresa_principal
                { wch: 40 }, // observacoes
                { wch: 15 }, // data_cadastro
                { wch: 15 }  // data_atualizacao
            ];

            sheet['!cols'] = columnWidths;

            // Destaca visualmente as instituições relacionadas com azul claro
            if (sheet['!ref']) {
                const range = XLSX.utils.decode_range(sheet['!ref']);
                relatedRowFlags.forEach((isRelated, rowIndex) => {
                    if (!isRelated) return;

                    const excelRow = rowIndex + 1; // +1 por conta do cabeçalho na linha 0
                    for (let col = 0; col <= range.e.c; col++) {
                        const cellAddress = XLSX.utils.encode_cell({ r: excelRow, c: col });
                        if (!sheet[cellAddress]) {
                            sheet[cellAddress] = { t: 's', v: '' };
                        }

                        sheet[cellAddress].s = {
                            fill: {
                                patternType: 'solid',
                                fgColor: { rgb: 'E3F2FD' }
                            }
                        };
                    }
                });
            }

            const workBook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workBook, sheet, sponsorsOnly ? 'Patrocinadores' : 'Filiados');

            const fileName = `${sponsorsOnly ? 'patrocinadores' : 'filiados'}-${new Date().toISOString().split('T')[0]}.xlsx`;
            await XLSX.writeFile(workBook, fileName);

            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'success',
                    msg: `Planilha gerada com sucesso! ${sheetData.length} registros exportados.`
                }
            });

        } catch (error) {
            console.error('Erro ao gerar Excel:', error);
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'error',
                    msg: 'Erro ao gerar planilha: ' + (error.message || 'Erro desconhecido')
                }
            });
        } finally {
            dispatch({ type: 'SET_EXPORTING_EXCEL', payload: false });
        }
    };

    // Função para mudança de página
    const handlePageChange = (event, newPage) => {
        dispatch({ type: 'SET_PAGINATION', payload: { page: newPage } });
        handleLoad(newPage);
    };

    // Handlers para formulário
    const handleFormChange = (field, value) => {
        dispatch({ type: 'UPDATE_FORM', payload: { [field]: value } });
    };

    const handleFilterChange = (field, value) => {
        dispatch({ type: 'SET_FILTERS', payload: { [field]: value } });
    };

    const handleCNPJChange = (value) => {
        const formatted = maskCNPJ(value);
        if (formatted.length <= 18) {
            handleFormChange('document', formatted);

            if (!state.dialog.editing) {
                dispatch({
                    type: 'UPDATE_FORM',
                    payload: {
                        social_reason: '',
                        state: '',
                        city: ''
                    }
                });
                dispatch({
                    type: 'SET_COMPANY_SEARCH_RESULT',
                    payload: {
                        form: {},
                        fieldsEnabled: false,
                        companyFound: false
                    }
                });
            }
        }
    };

    const handleCNPJBlur = () => {
        if (state.form.document.length === 18 && !state.dialog.editing) {
            searchCompanyByCNPJ(state.form.document);
        }
    };

    const handleBuscar = () => {
        dispatch({ type: 'SET_PAGINATION', payload: { page: 1 } });
        handleLoad(1);
    };

    const handleAddRelatedInstitution = () => {
        dispatch({
            type: 'UPDATE_FORM',
            payload: {
                instituicoesRelacionadas: [
                    ...(state.form.instituicoesRelacionadas || []),
                    createRelatedInstitution({ uf: state.form.state || '' })
                ]
            }
        });
    };

    const handleRemoveRelatedInstitution = (id) => {
        dispatch({
            type: 'UPDATE_FORM',
            payload: {
                instituicoesRelacionadas: (state.form.instituicoesRelacionadas || []).filter((item) => item.id !== id)
            }
        });
    };

    const handleRelatedInstitutionChange = (id, field, value) => {
        const updated = (state.form.instituicoesRelacionadas || []).map((item) => {
            if (item.id !== id) return item;
            const errors = { ...(item.errors || {}) };
            delete errors[field];
            return { ...item, [field]: value, errors };
        });

        dispatch({
            type: 'UPDATE_FORM',
            payload: { instituicoesRelacionadas: updated }
        });
    };

    useEffect(() => {
        handleLoad(1);
    }, []);

    useEffect(() => {
        if (sponsorsOnly || deepLinkHandledRef.current) return;

        const params = new URLSearchParams(location.search);
        const editAffiliateId = params.get('editAffiliateId');

        if (!editAffiliateId) return;

        deepLinkHandledRef.current = true;
        handleOpenDialog(Number(editAffiliateId));

        params.delete('editAffiliateId');
        const nextSearch = params.toString();
        navigate(
            {
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : ''
            },
            { replace: true }
        );
    }, [location.pathname, location.search, navigate, sponsorsOnly]);

    return (
        <Box sx={{ width: '100%', height: '100vh' }} p={3}>
            <Snackbar
                open={state.stateAlert.status}
                autoHideDuration={2500}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => dispatch({ type: 'CLOSE_ALERT' })}
            >
                <Alert sx={{ width: '100%' }} severity={state.stateAlert.type}>
                    {state.stateAlert.msg}
                </Alert>
            </Snackbar>

            <Typography variant="h5" mb={3}>
                {pageTitle}
            </Typography>

            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={2}>
                    <TextField
                        fullWidth
                        label="Nome da Empresa"
                        variant="outlined"
                        value={state.filters.name}
                        onChange={(e) => handleFilterChange('name', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sm={2}>
                    <TextField
                        fullWidth
                        label="CNPJ"
                        variant="outlined"
                        value={state.filters.document}
                        onChange={(e) => {
                            let cnpj = maskCNPJ(e.target.value);
                            if (cnpj.length <= 18) {
                                handleFilterChange('document', cnpj);
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                        <InputLabel id="filter-state-label">Estado</InputLabel>
                        <Select
                            value={state.filters.state}
                            onChange={(e) => handleFilterChange('state', e.target.value)}
                            labelId="filter-state-label"
                            label="Estado"
                        >
                            <MenuItem value="">
                                <em>Todos</em>
                            </MenuItem>
                            <MenuItem value="AC">AC</MenuItem>
                            <MenuItem value="AL">AL</MenuItem>
                            <MenuItem value="AP">AP</MenuItem>
                            <MenuItem value="AM">AM</MenuItem>
                            <MenuItem value="BA">BA</MenuItem>
                            <MenuItem value="CE">CE</MenuItem>
                            <MenuItem value="DF">DF</MenuItem>
                            <MenuItem value="ES">ES</MenuItem>
                            <MenuItem value="GO">GO</MenuItem>
                            <MenuItem value="MA">MA</MenuItem>
                            <MenuItem value="MT">MT</MenuItem>
                            <MenuItem value="MS">MS</MenuItem>
                            <MenuItem value="MG">MG</MenuItem>
                            <MenuItem value="PA">PA</MenuItem>
                            <MenuItem value="PB">PB</MenuItem>
                            <MenuItem value="PR">PR</MenuItem>
                            <MenuItem value="PE">PE</MenuItem>
                            <MenuItem value="PI">PI</MenuItem>
                            <MenuItem value="RJ">RJ</MenuItem>
                            <MenuItem value="RN">RN</MenuItem>
                            <MenuItem value="RS">RS</MenuItem>
                            <MenuItem value="RO">RO</MenuItem>
                            <MenuItem value="RR">RR</MenuItem>
                            <MenuItem value="SC">SC</MenuItem>
                            <MenuItem value="SP">SP</MenuItem>
                            <MenuItem value="SE">SE</MenuItem>
                            <MenuItem value="TO">TO</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                    <Autocomplete
                        freeSolo
                        options={citySuggestions}
                        loading={loadingCities}
                        value={state.filters.city}
                        onInputChange={(event, newValue) => {
                            handleFilterChange('city', newValue);
                            if (state.filters.state && newValue.length >= 3) {
                                searchCitiesByState(state.filters.state, newValue);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Cidade"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={2}>
                    <FormControl fullWidth>
                        <InputLabel id="plans-type">Tipo de Filiado:</InputLabel>
                        <Select
                            value={state.filters.type}
                            onChange={(e) => handleFilterChange('type', e.target.value)}
                            labelId="plans-type"
                            id="plans-type"
                            label="Tipo de Filiado:"
                        >
                            <MenuItem value="">
                                <em>Todos</em>
                            </MenuItem>
                            {Object.entries(availableAffiliateTypes).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={2}>
                    <TextField
                        fullWidth
                        label="Referência (Ano)"
                        onChange={(e) => handleFilterChange('reference', e.target.value)}
                        placeholder="Ex: 2026"
                    />
                </Grid>
                <Grid item xs={12} sm={1}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={handleBuscar}
                        startIcon={<Assessment />}
                    >
                        Buscar
                    </Button>
                </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 3 }}>
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleOpenDialog()}
                    startIcon={<Person />}
                >
                    {`Novo ${entityLabel}`}
                </Button>
                <Button
                    variant="contained"
                    color="info"
                    onClick={handleExcel}
                    startIcon={<FileDownload />}
                    disabled={state.exportingExcel || state.affiliates.length === 0}
                >
                    {state.exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                </Button>
            </Box>

            {state.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <TableInfo
                        title={tableTitle}
                        rows={state.affiliates}
                        variant="secondary"
                        edit={handleOpenDialog}
                        deactive={handleDelete}
                        getRowSx={(row) => row.attributes?.isRelatedInstitution ? {
                            backgroundColor: RELATED_ROW_BG,
                            '&:hover': {
                                backgroundColor: '#D7EBFC'
                            }
                        } : undefined}
                        columns={[
                            {
                                accessor: 'social_reason',
                                Header: 'Razão Social',
                                Cell: ({ value, row }) => (
                                    <Box>
                                        {row.original.attributes.isRelatedInstitution && (
                                            <Chip
                                                size="small"
                                                label="Relacionada"
                                                color="info"
                                                sx={{ mr: 1 }}
                                            />
                                        )}
                                        {value}
                                    </Box>
                                )
                            },
                            { accessor: 'document', Header: 'CNPJ' },
                            {
                                accessor: 'city', Header: 'Cidade',
                                Cell: ({ value, row }) => {
                                    const city = row.original.attributes?.city || value;
                                    return city || '-';
                                }
                            },
                            {
                                accessor: 'type',
                                Header: 'Tipo',
                                Cell: ({ value, row }) => {
                                    const type = row.original.attributes.affiliate?.data?.attributes?.type || value;
                                    return AFFILIATE_TYPES[type] || type || '-';
                                }
                            },
                            {
                                accessor: 'references',
                                Header: 'Referência',
                                Cell: ({ value, row }) => (
                                    <Box>
                                        {row.original.attributes.hasReference ? (
                                            <Chip
                                                size="small"
                                                label={value}
                                                color="success"
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label="Sem Referência"
                                                color="default"
                                            />
                                        )}
                                    </Box>
                                )
                            },
                            { accessor: 'active', Header: 'Filiado' },
                            { accessor: 'blocked', Header: 'Bloqueado' },
                            { accessor: 'actions', Header: 'Ações' }
                        ]}
                    />
                </Box>
            )}

            {!state.loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={state.pagination.pageCount}
                        page={state.pagination.page}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            <Dialog
                open={state.dialog.open}
                onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    {state.dialog.editing ? `Editar ${entityLabel}` : `Novo ${entityLabel}`}
                </DialogTitle>
                <DialogContent>
                    {state.dialog.editing?.attributes?.isRelatedInstitution && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Instituição Relacionada:</strong> Você pode editar apenas endereço, telefone, email e nome fantasia.
                            CNPJ e tipo de filiado não podem ser alterados.
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="CNPJ *"
                                fullWidth
                                required
                                disabled={state.dialog.editing?.attributes?.isRelatedInstitution}
                                value={state.form.document}
                                onChange={(e) => handleCNPJChange(e.target.value)}
                                onBlur={handleCNPJBlur}
                                InputProps={{
                                    endAdornment: state.searchingCompany && (
                                        <CircularProgress size={20} />
                                    )
                                }}
                                placeholder="00.000.000/0000-00"
                                helperText={
                                    state.dialog.editing?.attributes?.isRelatedInstitution
                                        ? "CNPJ não pode ser alterado para instituições relacionadas"
                                        : !state.dialog.editing
                                            ? "Digite o CNPJ completo para buscar os dados da empresa"
                                            : ""
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                label="Razão Social *"
                                fullWidth
                                required
                                disabled={state.dialog.editing?.attributes?.isRelatedInstitution || (!state.dialog.editing && !state.fieldsEnabled)}
                                value={state.form.social_reason}
                                onChange={(e) => handleFormChange('social_reason', e.target.value)}
                                helperText={
                                    state.dialog.editing?.attributes?.isRelatedInstitution
                                        ? "Razão Social não pode ser alterada para instituições relacionadas"
                                        : !state.dialog.editing && !state.fieldsEnabled
                                            ? "Campo será habilitado após busca do CNPJ"
                                            : ""
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Presidente"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.president}
                                onChange={(e) => handleFormChange('president', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome Fantasia"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.trade}
                                onChange={(e) => handleFormChange('trade', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Email"
                                type="email"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.email}
                                onChange={(e) => handleFormChange('email', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Telefone"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.phone}
                                onChange={(e) => handleFormChange('phone', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="WhatsApp"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.whatsapp}
                                onChange={(e) => handleFormChange('whatsapp', maskTEL(e.target.value))}
                                inputProps={{ maxLength: 15 }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="CEP"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.cep}
                                onChange={(e) => handleCep(e.target.value)}
                                InputProps={{
                                    endAdornment: state.searchingCEP && (
                                        <CircularProgress size={20} />
                                    )
                                }}
                                placeholder="00000-000"
                                helperText="Digite o CEP para preencher automaticamente o endereço"
                            />
                        </Grid>

                        <Grid item xs={12} sm={8}>
                            <TextField
                                label="Endereço"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.address}
                                onChange={(e) => handleFormChange('address', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Número"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.number}
                                onChange={(e) => handleFormChange('number', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Bairro"
                                fullWidth
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.district}
                                onChange={(e) => handleFormChange('district', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Estado *"
                                fullWidth
                                required
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.state}
                                onChange={(e) => handleFormChange('state', e.target.value)}
                                helperText={!state.dialog.editing && !state.fieldsEnabled ? "Campo será habilitado após busca do CNPJ" : ""}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Cidade *"
                                fullWidth
                                required
                                disabled={!state.dialog.editing && !state.fieldsEnabled}
                                value={state.form.city}
                                onChange={(e) => handleFormChange('city', e.target.value)}
                                helperText={!state.dialog.editing && !state.fieldsEnabled ? "Campo será habilitado após busca do CNPJ" : ""}
                            />
                        </Grid>
                        <Grid item xs={4} sm={4}>
                            <FormControlLabel
                                labelPlacement='top'
                                control={
                                    <Switch
                                        checked={state.form.active}
                                        disabled={!state.dialog.editing && state.form.document.length < 18}
                                        onChange={(e) => handleFormChange('active', e.target.checked)}
                                    />
                                }
                                label={state.form.active ? "Filiação Ativa" : "Filiação Inativa"}
                            />
                            <FormControlLabel
                                labelPlacement='top'
                                control={
                                    <Switch
                                        checked={state.form.blocked}
                                        disabled={!state.dialog.editing && state.form.document.length < 18}
                                        onChange={(e) => handleFormChange('blocked', e.target.checked)}
                                    />
                                }
                                label={state.form.blocked ? "Empresa Bloqueada" : "Empresa Desbloqueada"}
                            />
                        </Grid>
                        {!sponsorsOnly && 
                        <Grid item xs={12}>
                            <FormControl
                                component="fieldset"
                                disabled={state.dialog.editing?.attributes?.isRelatedInstitution || (!state.dialog.editing && state.form.document.length < 18)}
                            >
                                <FormLabel component="legend">
                                    Tipo de Filiado *
                                    {state.dialog.editing?.attributes?.isRelatedInstitution && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            (Não pode ser alterado para instituições relacionadas)
                                        </Typography>
                                    )}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={state.form.type}
                                    onChange={(e) => handleFormChange('type', e.target.value)}
                                >
                                    {Object.entries(availableAffiliateTypes).map(([key, label]) => (
                                        <FormControlLabel
                                            key={key}
                                            value={key}
                                            control={<Radio />}
                                            label={label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        }
                        {(sponsorsOnly || state.form.type === SPONSOR_TYPE) && (
                            <Grid item xs={!sponsorsOnly ? 12 : 6} sm={!sponsorsOnly ? 6 : 4}>
                                {sponsorsOnly ? (
                                    <FormControl fullWidth disabled={!state.dialog.editing && state.form.document.length < 18}>
                                        <InputLabel id="sponsor-quotas-label">Cota do Patrocinador</InputLabel>
                                        <Select
                                            labelId="sponsor-quotas-label"
                                            label="Cota do Patrocinador"
                                            value={state.form.quotas}
                                            onChange={(e) => handleFormChange('quotas', Number(e.target.value))}
                                        >
                                            <MenuItem value={0}>
                                                <em>Selecione</em>
                                            </MenuItem>
                                            {SPONSOR_QUOTA_OPTIONS.map((option) => (
                                                <MenuItem key={option.label} value={option.value}>
                                                    {option.label} | {option.value} inscrições
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        label="Quantidade de Cotas"
                                        type="number"
                                        fullWidth
                                        disabled={!state.dialog.editing && state.form.document.length < 18}
                                        value={state.form.quotas}
                                        onChange={(e) => handleFormChange('quotas', parseInt(e.target.value) || 0)}
                                    />
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                label="Observações"
                                fullWidth
                                multiline
                                rows={4}
                                disabled={!state.dialog.editing && state.form.document.length < 18}
                                value={state.form.observations}
                                onChange={(e) => handleFormChange('observations', e.target.value)}
                            />
                        </Grid>

                        {state.dialog.editing && !state.dialog.editing?.attributes?.isRelatedInstitution && (
                            <Grid item xs={12}>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        Instituições Relacionadas
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={handleAddRelatedInstitution}
                                        sx={{ mb: 2 }}
                                    >
                                        Adicionar instituição
                                    </Button>

                                    {(state.form.instituicoesRelacionadas || []).map((item) => (
                                        <Box key={item.id} sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, mb: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={3}>
                                                    <FormControl fullWidth error={!!item.errors?.tipo}>
                                                        <InputLabel id={`tipo-rel-${item.id}`}>Tipo *</InputLabel>
                                                        <Select
                                                            labelId={`tipo-rel-${item.id}`}
                                                            label="Tipo *"
                                                            value={item.tipo}
                                                            onChange={(e) => handleRelatedInstitutionChange(item.id, 'tipo', e.target.value)}
                                                        >
                                                            {RELATIONSHIP_TYPES.map((type) => (
                                                                <MenuItem key={type.value} value={type.value}>
                                                                    {type.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    {item.errors?.tipo && (
                                                        <Typography variant="caption" color="error">{item.errors.tipo}</Typography>
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="CNPJ *"
                                                        fullWidth
                                                        value={item.cnpj}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'cnpj', maskCNPJ(e.target.value))}
                                                        error={!!item.errors?.cnpj}
                                                        helperText={item.errors?.cnpj}
                                                        InputProps={{
                                                            endAdornment: item.loading ? <CircularProgress size={16} /> : null
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={5}>
                                                    <TextField
                                                        label="Nome da Empresa *"
                                                        fullWidth
                                                        value={item.nomeEmpresa}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'nomeEmpresa', e.target.value)}
                                                        error={!!item.errors?.nomeEmpresa}
                                                        helperText={item.errors?.nomeEmpresa}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Button color="error" onClick={() => handleRemoveRelatedInstitution(item.id)}>
                                                        Remover
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        label="Endereço *"
                                                        fullWidth
                                                        value={item.endereco}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'endereco', e.target.value)}
                                                        error={!!item.errors?.endereco}
                                                        helperText={item.errors?.endereco}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={3}>
                                                    <TextField
                                                        label="Cidade *"
                                                        fullWidth
                                                        value={item.cidade}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'cidade', e.target.value)}
                                                        error={!!item.errors?.cidade}
                                                        helperText={item.errors?.cidade}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={1}>
                                                    <TextField
                                                        label="UF *"
                                                        fullWidth
                                                        value={item.uf}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'uf', e.target.value.toUpperCase())}
                                                        error={!!item.errors?.uf}
                                                        helperText={item.errors?.uf}
                                                        inputProps={{ maxLength: 2 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        label="CEP *"
                                                        fullWidth
                                                        value={item.cep}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'cep', maskCEP(e.target.value))}
                                                        error={!!item.errors?.cep}
                                                        helperText={item.errors?.cep}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        label="Telefone *"
                                                        fullWidth
                                                        value={item.telefone}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'telefone', maskTEL(e.target.value))}
                                                        error={!!item.errors?.telefone}
                                                        helperText={item.errors?.telefone}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <TextField
                                                        label="Email *"
                                                        type="email"
                                                        fullWidth
                                                        value={item.email}
                                                        onChange={(e) => handleRelatedInstitutionChange(item.id, 'email', e.target.value)}
                                                        error={!!item.errors?.email}
                                                        helperText={item.errors?.email}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        )}



                        {!state.dialog.editing && state.companyFound && (
                            <Grid item xs={12}>
                                <Alert severity="success">
                                    Empresa encontrada! Os dados foram preenchidos automaticamente.
                                </Alert>
                            </Grid>
                        )}

                        {!state.dialog.editing && state.fieldsEnabled && !state.companyFound && (
                            <Grid item xs={12}>
                                <Alert severity="warning">
                                    Empresa não encontrada. Preencha os dados manualmente.
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch({ type: 'CLOSE_DIALOG' })} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleSave} color="success" variant="contained">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
